import * as React from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';

import {
  black,
  errorColor,
  gray100,
  gray80,
  gray90,
  primaryButtonOutlinedColor
} from '../../constants/Colors';

import type { FieldError } from 'react-hook-form';
import { Cancel, CloudUploadOutlined } from '@mui/icons-material';
import { Grid, Button, Box, IconButton, Chip } from '@mui/material';
import {
  DocumentDataType,
  ImageDataType
} from '../../validation/products/productType';
import noImage from '../assets/noImage.svg';
import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
  DropResult
} from 'react-beautiful-dnd';

type Props = {
  value: ImageDataType[] | DocumentDataType | string | null | undefined;
  onChange: (value: ImageDataType[] | DocumentDataType | string | null) => void;
  labelText?: string;
  supplementaryLabelText?: string;
  requiredText?: string;
  error?: boolean;
  helperText?: string | FieldError | undefined;
  width?: string;
  spWidth?: string;
  formMb?: string | undefined;
  fileType: string;
  resetError?: () => void;
};

const CustomFileUploader = (props: Props): React.ReactElement => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const {
    value,
    onChange,
    labelText,
    supplementaryLabelText,
    requiredText,
    error,
    helperText,
    formMb,
    fileType,
    resetError
  } = props;

  const styles = {
    formLabelWrapper: {
      fontSize: '12px',
      mb: '8px'
    },
    helper: {
      '@media screen and (max-width:600px)': {
        marginLeft: 0
      }
    },
    label: {
      fontSize: '14px'
    },
    required: {
      color: errorColor,
      fontSize: '14px',
      marginLeft: '8px'
    },
    formMb: { marginBottom: formMb ?? '8px' },
    fileUpload: {
      border: `1px dashed ${gray80}`,
      borderRadius: '4px'
    },
    fileSelectButton: {
      mt: '24px',
      mb: '8px',
      p: '5px 22px 5px 16px',
      fontSize: '14px',
      ...primaryButtonOutlinedColor
    },
    fileUploadText: {
      fontSize: '14px',
      color: gray90,
      mb: '24px',
      fontWeight: 500
    },
    supplementaryLabel: {
      fontSize: '12px',
      ml: '8px'
    },
    previewContainer: {
      mr: '16px',
      width: '100px',
      height: '100px',
      position: 'relative'
    },
    image: {
      maxWidth: '100%',
      maxHeight: '100%',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    },
    closeButton: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '18px',
      height: '18px',
      zIndex: 2,
      color: gray100,
      opacity: 0.8
    },
    mainImage: {
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100px',
      height: '23px',
      opacity: 0.8,
      backgroundColor: gray100,
      color: 'white',
      fontSize: '12px',
      textAlign: 'center',
      lineHeight: '24px'
    },
    chip: {
      p: '4px',
      color: gray100,
      fontSize: '13px',
      border: `1px solid ${gray80}`
    }
  };

  const setFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    setChangeFile(files);
  };

  const setDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setChangeFile(e.dataTransfer.files);
  };

  const setChangeFile = (files: FileList) => {
    if (fileType === 'images') {
      const cloneValue: ImageDataType[] =
        value != null ? [...(value as ImageDataType[])] : [];

      // filter 'del' kinds from cloneValue for counting
      const existingNewValues = cloneValue.filter(
        (image) => image.kind !== 'del'
      );
      const filesArray = Array.from(files);
      const vacancies = 3 - existingNewValues.length;
      const fileUploader = filesArray.slice(0, vacancies);

      // if there are no vacancies, terminate function
      if (vacancies <= 0) {
        return;
      }

      fileUploader.forEach((file, index) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const newImage = {
            id: null,
            kind: 'new',
            name: file.name,
            sort: (existingNewValues?.length ?? 0) + index + 1,
            binary: reader.result as string,
            size: file.size as number
          };
          cloneValue.push(newImage);
          onChange(cloneValue);
        };
      });
    } else if (fileType === 'document') {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        const newDocument = {
          kind: 'new',
          name: files[0].name,
          binary: reader.result as string,
          size: files[0].size as number
        };
        onChange(newDocument);
      };
    }
  };

  const setDeleteImage = (sort: number) => {
    if (sort === null) return;
    let cloneValue: ImageDataType[] =
      value != null ? [...(value as ImageDataType[])] : [];

    cloneValue = cloneValue
      .map((image) => {
        if (image.sort === sort) {
          if (image.kind === 'exist') {
            // 'exist'の場合に論理削除
            return { ...image, kind: 'del', sort: null };
          } else {
            // 'exist'でない場合（'new'）はnullに置き換えて物理削除
            return null;
          }
        }
        return image;
      })
      .filter((image) => image !== null) as ImageDataType[];

    const newImages = cloneValue
      .filter((image) => image.kind !== 'del')
      .map((image, i) => {
        return { ...image, sort: image.sort !== null ? i + 1 : null };
      });
    newImages.push(...cloneValue.filter((image) => image.kind === 'del'));

    onChange(newImages);
  };

  const setDeleteDocument = () => {
    resetError && resetError();
    onChange(null);
  };

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  const isButtonDisabled = () => {
    if (fileType === 'document') return Boolean(value);
    if (fileType === 'images') {
      if (!Array.isArray(value)) return;
      return (
        (value as ImageDataType[]).filter((image) => image.kind !== 'del')
          .length >= 3
      );
    }
  };

  const handleOmDragEnd: OnDragEndResponder = (result: DropResult) => {
    const draggingImageSortNum = Number(result.draggableId);
    const targetImageSortNum =
      result.destination?.index ?? draggingImageSortNum;
    if (draggingImageSortNum === targetImageSortNum) return;
    const cloneValue: ImageDataType[] =
      value != null ? [...(value as ImageDataType[])] : [];

    const newImages = cloneValue.map((image) => {
      if (image.sort === draggingImageSortNum) {
        image.sort = targetImageSortNum;
      } else {
        if (
          (image.sort as number) >= targetImageSortNum &&
          (image.sort as number) < draggingImageSortNum
        ) {
          image.sort = (image.sort as number) + 1;
        } else if (
          (image.sort as number) <= targetImageSortNum &&
          (image.sort as number) > draggingImageSortNum
        ) {
          image.sort = (image.sort as number) - 1;
        }
      }
      return image;
    });
    onChange(newImages);
  };

  const showDocumentLabelText = (data: DocumentDataType) => {
    if (!data.name) return '';
    if (data.name.length <= 45) return data.name;
    return data.name.slice(0, 45) + '...';
  };

  return (
    <Typography sx={styles.formMb} align="left" component="div">
      <Typography align="left" sx={styles.formLabelWrapper} component="div">
        <Typography color={black} sx={styles.label} component="span">
          {labelText}
        </Typography>
        {supplementaryLabelText && (
          <Typography
            color={gray90}
            sx={styles.supplementaryLabel}
            component="span"
          >
            {supplementaryLabelText}
          </Typography>
        )}
        {requiredText && (
          <Typography component="span" sx={styles.required}>
            {requiredText}
          </Typography>
        )}
      </Typography>
      {fileType === 'images' && value != null && (
        <DragDropContext onDragEnd={handleOmDragEnd}>
          <Droppable droppableId="images" direction="horizontal">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                <Grid container direction="row" mb="16px" mt="8px">
                  {(value as ImageDataType[])
                    .sort((a, b) => {
                      return (a.sort as number) - (b.sort ?? 0);
                    })
                    .map(
                      (image) =>
                        image.kind !== 'del' &&
                        image.sort !== null && (
                          <Draggable
                            key={image.sort}
                            draggableId={image.sort.toString()}
                            index={image.sort}
                          >
                            {(providedDraggable) => (
                              <Box
                                ref={providedDraggable.innerRef}
                                {...providedDraggable.draggableProps}
                                {...providedDraggable.dragHandleProps}
                              >
                                <Grid item sx={styles.previewContainer}>
                                  <IconButton
                                    sx={styles.closeButton}
                                    onClick={() =>
                                      setDeleteImage(image.sort as number)
                                    }
                                  >
                                    <Cancel />
                                  </IconButton>
                                  <Box
                                    component="img"
                                    src={
                                      image.filepath ?? image.binary ?? noImage
                                    }
                                    alt={image.name}
                                    sx={styles.image}
                                  />
                                  {image.sort === 1 && (
                                    <Typography sx={styles.mainImage}>
                                      メイン画像
                                    </Typography>
                                  )}
                                </Grid>
                              </Box>
                            )}
                          </Draggable>
                        )
                    )}
                  {provided.placeholder}
                </Grid>
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
      {fileType === 'document' && value != null && (
        <Grid container direction="row" mt="8px" mb="8px">
          <Chip
            label={showDocumentLabelText(value as DocumentDataType)}
            variant="outlined"
            onDelete={setDeleteDocument}
            sx={styles.chip}
          />
        </Grid>
      )}
      <Grid
        container
        direction="column"
        alignItems="center"
        sx={styles.fileUpload}
        onDrop={setDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <Grid item>
          <input
            type="file"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={setFileSelect}
            multiple={fileType === 'images'}
          />
          <Button
            sx={styles.fileSelectButton}
            variant="outlined"
            startIcon={<CloudUploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isButtonDisabled()}
          >
            ファイルを選択する
          </Button>
        </Grid>
        <Grid item>
          <Typography sx={styles.fileUploadText}>
            またはドラッグ&ドロップ
          </Typography>
        </Grid>
      </Grid>
      <FormHelperText sx={styles.helper} error={error}>
        {error && textfieldHelper()}
      </FormHelperText>
    </Typography>
  );
};
export default CustomFileUploader;
